import React from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { Link } from 'react-router-dom';

import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

import CustomFile from '@common/react/components/Forms/Files/File';
import { phoneRegexp } from '@common/react/utils/validationHelpers';
import { FileInterface, FileType } from '@common/typescript/objects/FileInterface';
import { List } from '@common/typescript/objects/List';

import { rest } from '@app/components/Api';
import FormikField from '@app/components/Forms/CustomFormikField/CustomFormikField';
import { CustomPhoneControl } from '@app/components/Forms/CustomPhoneControl/CustomPhoneControl';
import CustomSelect from '@app/components/Forms/CustomSelect/CustomSelect';
import SearchFieldAuto from '@app/components/Forms/SearchField/SearchFieldAuto';
import { GAeventSubmit } from '@app/components/LocalCommon/Utils/GAevents';
import { stateFilter } from '@app/components/LocalCommon/Utils/StateFilter';
import { numberBeautifyValidator, stringBeautifyValidator, zipCodeValidator } from '@app/components/LocalCommon/Utils/Validators';
import { onFileUpload } from '@app/components/LocalCommon/Utils/onFileUpload';
import { transformStateList } from '@app/components/LocalCommon/Utils/transformList';
import {
	Ailments,
	FormResponse,
	FormValues,
	PetSpecies,
	Sources,
} from '@app/components/Pages/Area/AreaObjects';
import AttachmentPreview from '@app/components/UI/Attachment/AttachmentPreview';
import CustomInput from '@app/components/UI/CustomInput/CustomInput';
import MiddleText from '@app/components/UI/MiddleText/MiddleText';
import { Area } from '@app/objects/Area';
import { ListOption } from '@app/objects/SidebarProps';
import { StatesListItem } from '@app/objects/StatesListItem';
import '@app/scss/localCommon/components/attachment.scss';

const validationSchema = Yup.object({
	firstName: stringBeautifyValidator('First Name is a required field'),
	lastName: stringBeautifyValidator('Last Name is a required field'),
	email: Yup.string().email('Email must be a valid Email').required('Email is a required field'),
	phone: Yup.string().required('Phone Number is a required field').matches(phoneRegexp, 'Invalid Phone Number'),
	streetLine1: stringBeautifyValidator('Street Address is a required field'),
	city: stringBeautifyValidator('City is a required field'),
	state: stringBeautifyValidator('State is a required field'),
	zip: zipCodeValidator(),
	gender: Yup.number().required('Pet\'s Gender is a required field').nullable(),
	petName: stringBeautifyValidator('Pet\'s Name is a required field'),
	petAge: numberBeautifyValidator('Pet\'s Age must be a number', 'Pet\'s Age must be greater than zero', 'Pet\'s Age is a required field', 'Pet\'s Age must be a whole number'),
	petWeight: numberBeautifyValidator('Pet\'s Weight must be a number', 'Pet\'s Weight must be greater than zero', 'Pet\'s Weight is a required field', 'Pet\'s Weight must be a whole number (1lb or more, with no decimals)'),
	petSpecies: Yup.string().nullable().required('Pet\'s Species is a required field'),
	captcha: Yup.string().required('Please check the box to submit this form'),
});

const FormSuccess = () => {
	React.useEffect(() => {
		window.scrollTo(0, 0);
		document.querySelector('#react-app')?.scrollTo(0, 0);
	}, []);

	return (
		<div className="area-request-form__success">
			<div className="area-request-form__success-checkmark">
				<img src={require('@app/images/checkmark_green.svg')} alt="success"/>
			</div>

			<h3>We're honored to help</h3>
			<h5>
				A Veterinary Care Coordinator will reach out to you from our support number&nbsp;
				<a className="get-in-touch-info__phone" href="tel:855-933-5683"><span>(855) 933-5683</span></a>.
			</h5>
			<h5>If you need more immediate assistance, you can call us 7 days a week.</h5>

			<h4>Visit the Lap of Love Resource Center</h4>
			<p>All information you could ever need about geriatric pets and how to care for them.</p>
			<Link className=" btn btn-md btn-primary" to="/resource-center">
				Go to Resource Center
			</Link>
		</div>
	);
};

interface Props {
	area: Area;
}

const AreaRequestForm: React.FC<Props> = ({area}) => {

	const gender = [
		{value: 1, label: 'Male'},
		{value: 2, label: 'Female'}
	];
	const aftercareOptions = [
		{value: 0, label: 'Private Cremation'},
		{value: 1, label: 'Communal Cremation'},
		{value: 2, label: 'Home Burial'},
		{value: 3, label: 'Family to handle aftercare'},
		{value: 4, label: 'Unsure at this time'}
	];

	const stateListOptions: ListOption[] = [];
	const sourcesListOptions: { value: number, label: string }[] = [];
	const [stateList, setStateList] = React.useState<StatesListItem[]>([]);
	const [species, setSpecies] = React.useState<PetSpecies[]>([]);
	const [ailments, setAilments] = React.useState<Ailments[]>([]);
	const [formSuccess, setFormSuccess] = React.useState<FormResponse>({success: false, error: ''});
	const [sources, setSources] = React.useState<Sources[]>([]);
	const [attachment, setAttachment] = React.useState<FileInterface[]>([]);
	const maxFile = 10000000;
	const imgExtension = ['jpg', 'jpeg', 'png'];
	const maxImgCount = 1;
	const [stateFormlabel, setStateFormLabel] = React.useState<string | number | undefined>('');
	const [sending, setSending] = React.useState(false);
	const [isConfirmed, setIsConfirmed] = React.useState<boolean>(false);

	const transformSources = () => {
		sources && sources.map((item, index) => {
			sourcesListOptions.push({value: item.id, label: item.name});
		});
	};
	React.useEffect(() => {
		rest.get<List<StatesListItem>>('v1/states').then(res => setStateList(res.list));
	}, []);

	transformStateList(stateList, stateListOptions);

	const requestSpecies = () => {
		rest.get<List<PetSpecies>>('v1/petspecies').then(res => setSpecies(res.list));
	};

	const requestAilments = () => {
		rest.get<List<Ailments>>('v1/ailments').then((res) => {
			const ailmentsSorted = res.list
				.sort((a, b) => a.name > b.name ? 1 : -1)
				.filter(item => item.name !== 'Other');
			setAilments(ailmentsSorted);
		});
	};

	const requestSources = () => {
		rest.get<List<Sources>>('v1/sources').then(res => setSources(res.list));
	};
	transformSources();

	return (
		!formSuccess.success ?
			<div className="area-request-form">
				<h1 className="area__title">{area.name}<br />Information Request Form</h1>
				<MiddleText title={''}
					text={'Lap of Love is here to help you and your pet with in-home euthanasia, veterinary hospice, telehospice, and mobile end-of-life care services. Please complete the form below and a Veterinary Care Coordinator will be in touch soon.'}/>
				<Formik
					initialValues={{
						firstName: '',
						lastName: '',
						email: '',
						phone: '',
						streetLine1: '',
						streetLine2: '',
						city: '',
						state: '',
						zip: '',
						source: null,
						petName: '',
						petSpecies: null,
						gender: null,
						petAge: null,
						petWeight: null,
						ailment: null,
						aftercare: null,
						comment: '',
						filePath: [],
						areaId: area.id,
						captcha: '',
					}}
					onSubmit={(values: FormValues, formikActions) => {
						setSending(true);
						rest.post<FormResponse>('v1/petregistration', values)
							.then((res) => {
								if (res.success) {
									formikActions.resetForm();
									setFormSuccess(res);
									setStateFormLabel('');
									GAeventSubmit('Area', 'Submit', area.name);
									setSending(false);
								} else {
									setFormSuccess(res);
								}
							});

					}}
					validationSchema={validationSchema}
					render={(formikBag) => {
						return (
							<Form>
								<MiddleText title="Contact information" text={''}/>
								<div className="area-request-form-group">
									<CustomInput name="firstName" placeholder="First Name*" formikBag={formikBag}/>
									<CustomInput name="lastName" placeholder="Last Name*" formikBag={formikBag}/>
									<CustomInput
										name="email"
										placeholder="Email Address*"
										formikBag={formikBag}
										removeSpace={true}
									/>
									<div className="phone">
										<FormikField
											title="Phone Number"
											fieldName="phone"
											render={fieldProps =>
												<CustomPhoneControl
													autocomplete="phone-number-user-doesnt-want-to-autofill"
													placeholder="Phone Number*"
													fieldProps={fieldProps}
												/>
											}
										/>
									</div>

								</div>
								<div className="area-request-form-group">
									<CustomInput name="streetLine1" placeholder="Street Address*"
												 formikBag={formikBag}/>
									<CustomInput name="streetLine2" placeholder="Street Address Line 2"
												 formikBag={formikBag}/>
									<CustomInput name="city" placeholder="City*" formikBag={formikBag}/>
									<SearchFieldAuto
										name="state"
										placeholder="State*"
										listOptions={stateListOptions}
										formikBag={formikBag}
										onSelect={(value, option) => {
											formikBag.setFieldValue('state', value);
											setStateFormLabel(option.label);
										}}
										label={stateFormlabel}
										onChange={(value) => {
											formikBag.setFieldValue('state', value);
											setStateFormLabel(value);
										}}
										searchButton={false}
										adjustValue
										setStateFormLabel={setStateFormLabel}
										customFilter={stateFilter}
									/>
									<CustomInput name="zip" placeholder="US Zip Code*" formikBag={formikBag}/>
								</div>
								<div className="area-request-form-group">
									<CustomSelect
										name="source"
										placeholder="How did you hear about Lap of Love?"
										onSelect={(value, option) => {
											formikBag.setFieldValue('source', value);
										}}
										onChange={(value, option) => {
											formikBag.setFieldValue('source', value);
										}}
										onSearch={(value, option) => {
											formikBag.setFieldValue('source', value);
										}}
										options={sourcesListOptions}
										showSearch={false}
										onFocus={requestSources}
										transform={false}
										formikBag={formikBag}

									/>
								</div>

								<MiddleText title="Pet information" text={''}/>
								<div className="area-request-form-group">
									<CustomInput name="petName" placeholder="Pet's Name*" formikBag={formikBag}/>
									<CustomSelect
										name="petSpecies"
										placeholder="Pet's Species*"
										onSelect={(value, option) => {
											formikBag.setFieldValue('petSpecies', value);
										}}
										onChange={(value, option) => {
											formikBag.setFieldValue('petSpecies', value);
										}}
										onSearch={(value, option) => {
											formikBag.setFieldValue('petSpecies', value);
										}}
										options={species}
										showSearch={false}
										onFocus={requestSpecies}
										transform={true}
										formikBag={formikBag}

									/>

									<CustomSelect
										name="gender"
										placeholder="Gender*"
										onSelect={(value, option) => {
											formikBag.setFieldValue('gender', value);
										}}
										onChange={(value, option) => {
											formikBag.setFieldValue('gender', value);
										}}
										onSearch={(value, option) => {
											formikBag.setFieldValue('gender', value);
										}}
										options={gender}
										showSearch={false}
										onFocus={() => {
										}}
										formikBag={formikBag}

									/>

									<CustomInput type="number" name="petAge" placeholder="Pet's Age*" formikBag={formikBag} innerIcon="Yrs" />
									<CustomInput type="number" name="petWeight" placeholder="Pet's Weight*" formikBag={formikBag} innerIcon="Lbs" />
								</div>

								<div className="area-request-form-group">
									<div>
										<h5>What is your pet's main ailment?</h5>
										<CustomSelect
											name="ailment"
											onSelect={(value, option) => {
												formikBag.setFieldValue('ailment', value);
											}}
											onChange={(value, option) => {
												formikBag.setFieldValue('ailment', value);
											}}
											onSearch={(value, option) => {
												formikBag.setFieldValue('ailment', value);
											}}
											options={ailments}
											showSearch={true}
											onFocus={requestAilments}
											transform={true}
											formikBag={formikBag}
										/>
									</div>
									<div>
										<h5>What type of aftercare would you prefer?</h5>
										<CustomSelect
											name="aftercare"
											onSelect={(value, option) => {
												formikBag.setFieldValue('aftercare', value);
											}}
											onChange={(value, option) => {
												formikBag.setFieldValue('aftercare', value);
											}}
											onSearch={(value, option) => {
												formikBag.setFieldValue('aftercare', value);
											}}
											options={aftercareOptions}
											showSearch={false}
											onFocus={() => {
											}}
											formikBag={formikBag}
										/>
									</div>
								</div>
								<div className="area-request-form-group area-request-form-group_textarea">
									<CustomInput
										component={'textarea'}
										name="comment"
										placeholder="Any additional comments or concerns?"
										formikBag={formikBag}
									/>
								</div>
								<div className="area-request-form-group">
									<Field
										name="filePath"
										render={(field) => {
											return (
												<div className={field.form.errors['filePath'] && field.form.touched['filePath'] ?
													'attachment modal-wrap attachment_cropped' :
													'attachment modal-wrap'}
												>
													<h4 className="attachment-title">
														Would you like to send us an image of your pet?
													</h4>
													<AttachmentPreview
														attachment={attachment}
														setAttachment={setAttachment}
														formikBag={formikBag}
													/>
													<CustomFile
														update={data => onFileUpload(
															data,
															field,
															formikBag,
															'filePath',
															maxFile,
															imgExtension,
															attachment,
															maxImgCount,
															setAttachment,
														)}
														type="jobInquiry"
														fileType={FileType.File}
														noRelation={true}
														asButton buttonClassName="btn btn-secondary"
														buttonCaption="Upload a file"
														multiple={true}
													/>
													{
														field.form.errors['filePath'] && field.form.touched['filePath'] ?
															<div
																className="custom-input__error error_top-gap"> {field.form.errors['filePath']}</div> : null
													}
												</div>
											);
										}}
									/>
								</div>
								<div className="area__news">
									By providing a telephone number and submitting the form you are consenting to be contacted by SMS text message by Lap of Love.
									Message & data rates may apply. Reply STOP to opt out of further messaging.
								</div>
								<div className="area-request-form-group area-request-form-group_recaptcha">
									<FormikField
										containerClassName="captcha"
										fieldName="captcha"
										render={fieldProps =>
											<ReCAPTCHA
												sitekey="6LezTMIcAAAAACaXvLNse5bmTJpwqs0Ezm_bhTrU"
												onChange={(value) => {
													if (value) {
														formikBag.setFieldValue('captcha', value);
														setIsConfirmed(true);
													} else {
														formikBag.setFieldValue('captcha', '');
														setIsConfirmed(false);
														setSending(false);
													}
												}}
												onExpired={() => formikBag.setFieldValue('captcha', 'expired')}
												hl="en"
											/>
										}
									/>
								</div>
								<div className="area-request-form-group area-request-form-group_submit">
									<button
										type="submit"
										className="btn btn-md btn-primary"
										disabled={sending}
									>
										Submit
									</button>
									{
										Object.values(formikBag.errors).some(el => el) &&
										formikBag.submitCount > 0 &&
										<div className="submit-validate-error">Please fill in all required fields marked with an asterisk</div>

									}
									{
										sending ?
											<div className="lds-ring">
												<div/>
												<div/>
												<div/>
												<div/>
											</div>
											: null
									}
								</div>

								<div className="area-request-form-group area-request-form-group_submit">
									{
										formSuccess.error ? <div className="submit-validate-error">{formSuccess.error}</div> : null
									}
								</div>

							</Form>
						);
					}}
				/>
			</div> : <FormSuccess/>
	);
};

export default AreaRequestForm;
